// Price Box landmine highlighting — the mark resolver (CR-20260904-001, Presentation, units 5.1/5.2).
//
// One question, one answer: given a Price Box column's date label, the calendar's verdict for that
// date, and the three per-user switches, WHICH SINGLE BACKGROUND does that column get?
//
// Presentation only. Nothing here reads a run, an order or a position, and nothing here is reachable
// from the ORION skill (BR-P3). The switches enter in exactly one place — the `switches` argument —
// and leave as one string that becomes an attribute on a <td>.
//
// Pure / Angular-free so it is trivially unit-testable and carries no module dependency.
// ES5 note: arrays + indexOf only (no Set / for..of), matching the rest of the copilot logic.
//
// ⚠ NO 'buffer' MARK. A fourth grey mark for "the two days behind a landmine" existed for one day
// (2026-09-07, O58) and was rolled back with the buffer rule itself on 2026-09-08: there are no
// buffer days, so there is nothing to paint. This module imports nothing from the copilot again.

// The column's background, one value. There is ONE background per column (BR-P2), so the precedence
// among the ENABLED marks lives here and nowhere else.
export type LandmineMark = 'monday' | 'tier1' | 'tier2';

// The three per-user switches, snapshotted by the host. Never read from a template binding: the
// settings getter JSON-clones the whole APG blob on every read.
export interface LandmineHighlightSwitches {
    highlightMondays: boolean;
    highlightTier1: boolean;
    highlightTier2: boolean;
}

// The calendar's closed tier vocabulary (contract C1 section 2). Repeated here rather than imported
// so this module stays free of a cross-team dependency; the real LandmineDayMap satisfies the port
// below structurally, and the host proves it at compile time by passing the real map in.
export type LandmineTierValue = 'T1' | 'T2' | 'clear' | 'unknown';

// A one-field structural port onto the Events Calendar's LandmineDayMap. Deliberately the SMALLEST
// shape that answers the colour question: a synchronous read of one date's tier.
//
// `undefined` = this map does not answer for that date. It is NOT "clear" — it paints nothing, the
// same as 'unknown' (C1 G1).
export interface LandmineVerdictLookup {
    get(dateKey: string): { tier: LandmineTierValue } | undefined;
}

// The per-column answer, keyed by the Price Box's own "dd-MMM-yy" label. A label with no entry has
// no mark; the template reads a miss as "no colour".
export interface LandmineMarkMap {
    [expirationLabel: string]: LandmineMark;
}

const MONTHS: string[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * "11-Sep-26" (the Price Box's own column label, header and body alike) -> a local-midnight Date,
 * or null when the label is not a real calendar date.
 *
 * Local, not UTC: expirationDayKey() reads local Y/M/D, so a local-midnight Date maps back onto its
 * own day key in every timezone.
 *
 * Cause -> effect: Monday is derived from THIS, never from the header's day-of-week token. That
 * token comes from a string split and renders the literal text "undefined" on the short-form path,
 * which would silently cost every Monday its grey.
 */
export function parsePriceboxExpirationLabel(label: string): Date | null {
    if (typeof label !== 'string') {
        return null;
    }

    const parts = label.split('-');
    if (parts.length !== 3) {
        return null;
    }

    const day = parseInt(parts[0], 10);
    const monthIx = MONTHS.indexOf(parts[1].toLowerCase());
    let year = parseInt(parts[2], 10);

    if (isNaN(day) || monthIx < 0 || isNaN(year)) {
        return null;
    }

    if (year < 100) {
        year = 2000 + year;
    }

    const date = new Date(year, monthIx, day);

    // Rejects rollovers such as "31-Feb-26", which JS would silently accept as 3 March.
    if (date.getFullYear() !== year || date.getMonth() !== monthIx || date.getDate() !== day) {
        return null;
    }

    return date;
}

// Local "yyyy-MM-dd" day key — the join key onto the calendar, the same shape the backend emits for
// OptionExpirationDescriptor.optionExpirationDate. Kept identical to expirationDayKey() in
// copilot-shared/expiration-dates.ts, which is Input validation's file (imported, never edited).
export function priceboxDayKey(d: Date): string {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return y + '-' + (m < 10 ? '0' + m : '' + m) + '-' + (day < 10 ? '0' + day : '' + day);
}

/**
 * The decision. One background per column, so exactly one mark wins.
 *
 * Precedence among the ENABLED marks: red, then orange, then grey — the interim reading of owner
 * question O9 (C1 Q9). It is a PLACEHOLDER, not a derived rule: nothing in the rules says a landmine
 * outranks a Monday. When Chris rules, this function is the one line that changes.
 *
 * A switch changes the WINNER, not only the visibility: a day that is both a Monday and Tier 1, with
 * Tier 1 off and Mondays on, is grey — the red is not on the board to win.
 */
export function resolveLandmineMark(
    isMonday: boolean,
    tier: LandmineTierValue | undefined,
    switches: LandmineHighlightSwitches
): LandmineMark | null {

    if (!switches) {
        return null;
    }

    if (switches.highlightTier1 && tier === 'T1') {
        return 'tier1';
    }

    if (switches.highlightTier2 && tier === 'T2') {
        return 'tier2';
    }

    if (switches.highlightMondays && isMonday) {
        return 'monday';
    }

    // 'clear', 'unknown', an unanswered date, or every relevant switch off.
    return null;
}

/**
 * The whole column set in one pass. Returns a plain dictionary keyed by the column label, so the
 * template's per-cell read is a dictionary lookup and nothing else — no allocation, no differ.
 *
 * Never throws. A missing lookup (the calendar has not answered yet, or is unavailable) still marks
 * Mondays: Monday is derived from the column's own date, so grey survives the calendar being down.
 * An unparseable label is skipped, not guessed at.
 */
export function buildLandmineMarks(
    columnLabels: string[],
    lookup: LandmineVerdictLookup | null | undefined,
    switches: LandmineHighlightSwitches
): LandmineMarkMap {

    const marks: LandmineMarkMap = {};

    if (!columnLabels || !switches) {
        return marks;
    }

    for (let i = 0; i < columnLabels.length; i++) {

        const label = columnLabels[i];
        if (!label || marks[label]) {
            continue;
        }

        const date = parsePriceboxExpirationLabel(label);
        if (!date) {
            continue;
        }

        const isMonday = date.getDay() === 1;

        const dayKey = priceboxDayKey(date);

        let tier: LandmineTierValue | undefined = undefined;
        if (lookup) {
            const verdict = lookup.get(dayKey);
            tier = verdict ? verdict.tier : undefined;
        }

        const mark = resolveLandmineMark(isMonday, tier, switches);
        if (mark) {
            marks[label] = mark;
        }
    }

    return marks;
}
